import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { HardHat, Eye, EyeSlash, Fingerprint } from "@phosphor-icons/react";
import { CameraCapture } from "../components/CameraCapture";

const BG = "https://images.unsplash.com/photo-1713593930871-e21d7f9ef4a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwd29ya2Vyc3xlbnwwfHx8fDE3ODY0NzgyNDN8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register, user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [form, setForm] = useState({ name: "", email: "", password: "", inviteCode: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState("correo");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [faceImg, setFaceImg] = useState(null);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const submitFace = async (e) => {
    e.preventDefault();
    if (!faceImg) return toast.error("Toma o sube una foto de tu rostro");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("email", form.email);
      fd.append("photo", faceImg);
      const { data } = await api.post("/auth/face/login", fd);
      setUser(data);
      toast.success("Bienvenido");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ name: form.name, email: form.email, password: form.password, invite_code: form.inviteCode || null });
      toast.success("Bienvenido");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email, channel });
      const ch = (data.channels || []);
      if (ch.length) toast.success(`Código enviado por ${ch.join(" y ")}. Revisa tu ${ch.includes("correo") ? "correo" : "WhatsApp"}.`);
      else toast.success(data.message || "Si el correo existe, enviamos un código.");
      setMode("reset");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: form.email, code, new_password: newPassword });
      toast.success("Contraseña actualizada. Ya puedes iniciar sesión.");
      setMode("login"); setCode(""); setNewPassword("");
      setForm({ ...form, password: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const titles = {
    login: { h: "Iniciar sesión", p: "Accede a tu panel de obra." },
    register: { h: "Crear cuenta", p: "Regístrate como contratista." },
    forgot: { h: "Recuperar contraseña", p: "Te enviaremos un código por correo o WhatsApp." },
    reset: { h: "Nueva contraseña", p: "Escribe el código que recibiste y tu nueva contraseña." },
    face: { h: "Entrar con tu rostro", p: "Escribe tu correo y verifica tu identidad con la cámara." },
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block w-1/2 relative">
        <img src={BG} alt="Obra" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F172A]/70" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="overline !text-white/70 mb-3">Control de obra · IA</div>
          <h1 className="font-heading font-black text-4xl xl:text-5xl leading-none tracking-tighter">
            Controla cada contratista, pago y avance de tu obra.
          </h1>
          <p className="mt-4 text-white/80 max-w-md">
            Analiza contratos con IA, evita sobre-estimaciones y controla el avance, la calidad y las finanzas de tu obra.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-secondary">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <HardHat size={24} weight="bold" className="text-white" />
            </div>
            <span className="font-heading font-extrabold text-xl tracking-tight">Obra en Control</span>
          </div>
          <h2 className="font-heading font-bold text-2xl mb-1">{titles[mode].h}</h2>
          <p className="text-sm text-muted-foreground mb-6">{titles[mode].p}</p>

          {(mode === "login" || mode === "register") && (
            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <label className="text-sm font-medium">Nombre</label>
                  <input data-testid="name-input" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full h-11 px-3 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none" />
                </div>
              )}
              <div>
                <label className="text-sm font-medium">Correo</label>
                <input data-testid="email-input" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium">Contraseña</label>
                <div className="relative">
                  <input data-testid="password-input" type={show ? "text" : "password"} required value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1 w-full h-11 px-3 pr-10 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3.5 text-muted-foreground">
                    {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {mode === "login" && (
                  <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")}
                    className="mt-2 text-sm text-primary font-semibold hover:underline">
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>
              {mode === "register" && (
                <div>
                  <label className="text-sm font-medium">Código de invitación <span className="text-muted-foreground">(opcional)</span></label>
                  <input data-testid="invite-code-input" value={form.inviteCode}
                    onChange={(e) => setForm({ ...form, inviteCode: e.target.value })}
                    placeholder="Ej. GERENTE-A1B2"
                    className="mt-1 w-full h-11 px-3 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none uppercase" />
                  <p className="text-xs text-muted-foreground mt-1">Si tu administrador te dio un código, asignará tu rol automáticamente. Sin código entras como contratista.</p>
                </div>
              )}
              <button data-testid="submit-auth-btn" type="submit" disabled={busy}
                className="w-full h-11 bg-primary text-white font-semibold rounded-sm hover:bg-orange-600 transition-colors duration-200 disabled:opacity-60">
                {busy ? "Procesando…" : mode === "login" ? "Entrar" : "Crear cuenta"}
              </button>
              {mode === "login" && (
                <button type="button" data-testid="face-mode-btn" onClick={() => setMode("face")}
                  className="w-full h-11 border border-primary/40 text-primary font-semibold rounded-sm hover:bg-primary/5 transition-colors duration-200 inline-flex items-center justify-center gap-2">
                  <Fingerprint size={18} weight="bold" /> Entrar con reconocimiento facial
                </button>
              )}
            </form>
          )}

          {mode === "face" && (
            <form onSubmit={submitFace} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Correo</label>
                <input data-testid="face-email-input" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium">Tu rostro</label>
                <div className="mt-1"><CameraCapture onCapture={setFaceImg} /></div>
              </div>
              <button data-testid="face-login-btn" type="submit" disabled={busy}
                className="w-full h-11 bg-primary text-white font-semibold rounded-sm hover:bg-orange-600 transition-colors duration-200 disabled:opacity-60">
                {busy ? "Verificando…" : "Entrar con mi rostro"}
              </button>
              <button type="button" onClick={() => { setMode("login"); setFaceImg(null); }} className="w-full text-sm text-muted-foreground hover:text-primary">← Volver a contraseña</button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={submitForgot} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Correo de tu cuenta</label>
                <input data-testid="forgot-email-input" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium">¿Cómo quieres recibir el código?</label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[{ v: "correo", l: "Correo" }, { v: "whatsapp", l: "WhatsApp" }, { v: "ambos", l: "Ambos" }].map((o) => (
                    <button type="button" key={o.v} data-testid={`channel-${o.v}`} onClick={() => setChannel(o.v)}
                      className={`h-10 rounded-sm border text-sm font-semibold transition-colors duration-150 ${channel === o.v ? "bg-primary text-white border-primary" : "border-input bg-white text-foreground hover:border-primary"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">WhatsApp requiere que tengas un teléfono registrado por tu administrador.</p>
              </div>
              <button data-testid="send-code-btn" type="submit" disabled={busy}
                className="w-full h-11 bg-primary text-white font-semibold rounded-sm hover:bg-orange-600 transition-colors duration-200 disabled:opacity-60">
                {busy ? "Enviando…" : "Enviar código"}
              </button>
              <button type="button" onClick={() => setMode("reset")} className="w-full text-sm text-muted-foreground hover:text-primary">Ya tengo un código</button>
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={submitReset} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Correo de tu cuenta</label>
                <input data-testid="reset-email-input" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium">Código de recuperación</label>
                <input data-testid="reset-code-input" required value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="6 dígitos"
                  className="mt-1 w-full h-11 px-3 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none tracking-widest" />
              </div>
              <div>
                <label className="text-sm font-medium">Nueva contraseña</label>
                <input data-testid="reset-newpw-input" type={show ? "text" : "password"} required minLength={6} value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none" />
              </div>
              <button data-testid="reset-submit-btn" type="submit" disabled={busy}
                className="w-full h-11 bg-primary text-white font-semibold rounded-sm hover:bg-orange-600 transition-colors duration-200 disabled:opacity-60">
                {busy ? "Actualizando…" : "Cambiar contraseña"}
              </button>
            </form>
          )}

          <p className="text-sm text-muted-foreground mt-6">
            {(mode === "login" || mode === "register") ? (
              <>
                {mode === "login" ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}{" "}
                <button data-testid="toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="text-primary font-semibold hover:underline">
                  {mode === "login" ? "Regístrate" : "Inicia sesión"}
                </button>
              </>
            ) : (
              <button data-testid="back-to-login-btn" onClick={() => setMode("login")} className="text-primary font-semibold hover:underline">
                ← Volver a iniciar sesión
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
